import React, { useState } from 'react';
import { supabase } from 'lib/supabase';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import { Plus, Link, QrCode, Settings, Copy, Download } from 'lucide-react';
import QRCode from 'react-qr-code';

const LinkCreationWizard = ({ campaigns, onLinkCreated, isCreating, onCreateNew }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    target_url: '',
    campaign_id: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    expires_at: ''
  });
  const [generatedLink, setGeneratedLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [qrCodeSize, setQrCodeSize] = useState(200);
  const [showQROptions, setShowQROptions] = useState(false);

  const validateStep = (stepNumber) => {
    const newErrors = {};
    
    switch (stepNumber) {
      case 1:
        if (!formData?.title?.trim()) newErrors.title = 'Le titre est requis';
        if (!formData?.target_url?.trim()) newErrors.target_url = 'L\'URL cible est requise';
        else if (!isValidUrl(formData?.target_url)) newErrors.target_url = 'URL invalide';
        break;
      case 2:
        // UTM parameters are optional
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const generateShortUrl = () => {
    const randomId = Math.random()?.toString(36)?.substring(2, 10);
    return `https://artis.io/t/${randomId}`;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    setLoading(true);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) {
        throw new Error('Utilisateur non connecté');
      }

      const shortUrl = generateShortUrl();
      
      const linkData = {
        created_by: session?.user?.id,
        campaign_id: formData?.campaign_id || null,
        title: formData?.title,
        description: formData?.description || null,
        target_url: formData?.target_url,
        short_url: shortUrl,
        utm_source: formData?.utm_source || null,
        utm_medium: formData?.utm_medium || null,
        utm_campaign: formData?.utm_campaign || null,
        utm_term: formData?.utm_term || null,
        utm_content: formData?.utm_content || null,
        expires_at: formData?.expires_at || null,
        status: 'active'
      };

      const { data, error } = await supabase?.from('tracking_links')?.insert(linkData)?.select(`
          id, title, description, short_url, target_url, 
          total_clicks, unique_clicks, status, created_at,
          campaigns(name)
        `)?.single();

      if (error) {
        throw error;
      }

      setGeneratedLink(data);
      setStep(3);
      onLinkCreated?.(data);

    } catch (error) {
      console.error('Error creating tracking link:', error);
      setErrors({ submit: 'Erreur lors de la création du lien' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard?.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas');
    if (canvas) {
      const url = canvas?.toDataURL();
      const link = document.createElement('a');
      link.download = `qr-code-${formData?.title?.replace(/\s+/g, '-') || 'link'}.png`;
      link.href = url;
      link?.click();
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      target_url: '',
      campaign_id: '',
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_term: '',
      utm_content: '',
      expires_at: ''
    });
    setGeneratedLink(null);
    setStep(1);
    setErrors({});
  };

  if (!isCreating && step === 1) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <Link className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Créer un nouveau lien de suivi
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Générez des liens personnalisés avec QR codes pour suivre les performances
          </p>
          <Button 
            onClick={onCreateNew}
            className="mt-4"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer un lien
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Progress Steps */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center">
          {[1, 2, 3]?.map((num) => (
            <div key={num} className="flex items-center">
              <div className={`
                flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                ${step >= num ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'}
              `}>
                {num}
              </div>
              {num < 3 && (
                <div className={`
                  w-16 h-1 mx-2
                  ${step > num ? 'bg-indigo-600' : 'bg-gray-200'}
                `} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-sm">
          <span className={step >= 1 ? 'text-indigo-600' : 'text-gray-500'}>
            Informations
          </span>
          <span className={step >= 2 ? 'text-indigo-600' : 'text-gray-500'}>
            Paramètres UTM
          </span>
          <span className={step >= 3 ? 'text-indigo-600' : 'text-gray-500'}>
            Lien généré
          </span>
        </div>
      </div>
      <div className="p-6">
        {/* Step 1: Basic Information */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Informations du lien
            </h3>
            
            <Input
              label="Titre du lien *"
              value={formData?.title || ''}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              placeholder="ex: Concert Piano - Martin Dubois"
              error={errors?.title}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData?.description || ''}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Description optionnelle du lien"
              />
            </div>

            <Input
              label="URL de destination *"
              value={formData?.target_url || ''}
              onChange={(e) => handleInputChange('target_url', e?.target?.value)}
              placeholder="https://artisio.com/events/concert-piano"
              error={errors?.target_url}
            />

            <Select
              label="Campagne associée"
              value={formData?.campaign_id || ''}
              onChange={(value) => handleInputChange('campaign_id', value)}
              options={[
                { value: '', label: 'Aucune campagne' },
                ...(campaigns?.map(campaign => ({
                  value: campaign?.id,
                  label: campaign?.name
                })) || [])
              ]}
            />

            <Input
              label="Date d'expiration"
              type="datetime-local"
              value={formData?.expires_at || ''}
              onChange={(e) => handleInputChange('expires_at', e?.target?.value)}
            />

            <div className="flex justify-end pt-4">
              <Button onClick={handleNext}>
                Suivant
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: UTM Parameters */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Paramètres UTM (optionnel)
              </h3>
              <Settings className="h-5 w-5 text-gray-400" />
            </div>
            
            <p className="text-sm text-gray-600">
              Ajoutez des paramètres UTM pour un suivi plus précis dans Google Analytics
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="UTM Source"
                value={formData?.utm_source || ''}
                onChange={(e) => handleInputChange('utm_source', e?.target?.value)}
                placeholder="facebook, instagram, agent_link"
              />

              <Input
                label="UTM Medium"
                value={formData?.utm_medium || ''}
                onChange={(e) => handleInputChange('utm_medium', e?.target?.value)}
                placeholder="social, email, qr_code"
              />

              <Input
                label="UTM Campaign"
                value={formData?.utm_campaign || ''}
                onChange={(e) => handleInputChange('utm_campaign', e?.target?.value)}
                placeholder="summer_tour_2024"
              />

              <Input
                label="UTM Term"
                value={formData?.utm_term || ''}
                onChange={(e) => handleInputChange('utm_term', e?.target?.value)}
                placeholder="concert_piano"
              />
            </div>

            <Input
              label="UTM Content"
              value={formData?.utm_content || ''}
              onChange={(e) => handleInputChange('utm_content', e?.target?.value)}
              placeholder="banner_top, link_footer"
            />

            {errors?.submit && (
              <div className="text-red-600 text-sm">{errors?.submit}</div>
            )}

            <div className="flex justify-between pt-4">
              <Button
                variant="outline"
                onClick={handleBack}
              >
                Précédent
              </Button>
              <Button
                onClick={handleSubmit}
                loading={loading}
              >
                Générer le lien
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Generated Link */}
        {step === 3 && generatedLink && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <QrCode className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Lien généré avec succès !
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Votre lien de suivi et QR code sont prêts à être utilisés
              </p>
            </div>

            {/* Generated Short URL */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lien court généré
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={generatedLink?.short_url || ''}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(generatedLink?.short_url || '')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* QR Code */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  QR Code
                </label>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowQROptions(!showQROptions)}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadQRCode}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {showQROptions && (
                <div className="mb-4">
                  <label className="block text-sm text-gray-600 mb-2">
                    Taille: {qrCodeSize}px
                  </label>
                  <input
                    type="range"
                    min="150"
                    max="400"
                    value={qrCodeSize}
                    onChange={(e) => setQrCodeSize(parseInt(e?.target?.value))}
                    className="w-full"
                  />
                </div>
              )}

              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-lg">
                  <QRCode
                    id="qr-code-canvas"
                    value={generatedLink?.short_url || ''}
                    size={qrCodeSize}
                    level="M"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                variant="outline"
                onClick={resetForm}
              >
                Créer un autre lien
              </Button>
              <Button
                onClick={() => window.location.href = '/link-analytics'}
              >
                Voir les analyses
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkCreationWizard;